import _ from 'lodash';

import {
  globalWindow,
  IframeFeaturePoliciesEnum,
} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {tryParseUrl} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {randomString} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {FrontConfig} from '../../../srcCommon/bundler/bundlerDefineConfig';
import {
  ApplicationDefaultError,
  ApplicationError,
  EntryPointTypesEnum,
  HttpVerbsEnum,
} from '../../applications/bridge/v2/types/bridgeTypesV2';
import {FrameTypesEnum} from '../../models/api/apiTypes';
import {ApplicationEntryPointActionFeaturesEnum} from '../../models/api/applicationEntryPointActionModel';
import {LegacyApplicationModel} from '../../models/api/legacyApplicationModel';
import {debug} from '../../services/debug';
import {RequestMethodsEnum} from '../../services/http/httpBase';
import {HttpError, HttpResponseError} from '../../services/http/httpError';
import {allowedProtocols} from '../../store/stateless/navigationActions';
import {IOTSError} from '../apiHelpers';
import {CancelError} from '../asyncCancelHelpers';

/*
 * URls and IDs.
 */

export function buildLegacyApplicationIframeOrigin(application: LegacyApplicationModel) {
  return `https://${application.typeId}.${FrontConfig.APPLICATION_RUNTIME_HOST}`;
}

export function buildLegacyAppEngineIframeUrl(application: LegacyApplicationModel) {
  return buildLegacyApplicationIframeOrigin(application) + FrontConfig.APPLICATION_RUNTIME_ENGINE_PATH;
}

export function buildLegacyAppViewIframeUrl(application: LegacyApplicationModel) {
  return buildLegacyApplicationIframeOrigin(application) + FrontConfig.APPLICATION_RUNTIME_VIEW_PATH;
}

export function buildLegacyAppEngineIframeName(application: LegacyApplicationModel) {
  return `app_engine_${application.id}`;
}

export function buildLegacyAppPreloadViewIframeName(application: LegacyApplicationModel) {
  return `app_preload_view_${application.id}`;
}

export function buildLegacyAppViewIframeName(widgetId: string) {
  return `app_view_${widgetId}`;
}

export const legacyAppIframePermissions = [
  // Allows the content to be treated as being from its normal origin.
  'allow-same-origin',
  // Allows the embedded browsing context to run javascript.
  'allow-scripts',
  // Allows forms to be submitted.
  'allow-forms',
  // Allows window to be open. The window will inherit the sandbox permissions.
  'allow-popups',
].join(' ');

const legacyApplicationFeaturesToIframeFeaturePoliciesMap: {
  [K in ApplicationEntryPointActionFeaturesEnum]?: ReadonlyArray<IframeFeaturePoliciesEnum>;
} = {
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_CAMERA]: [IframeFeaturePoliciesEnum.CAMERA],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_FULLSCREEN]: [IframeFeaturePoliciesEnum.FULLSCREEN],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_MICROPHONE]: [IframeFeaturePoliciesEnum.MICROPHONE],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_CLIPBOARD]: [
    IframeFeaturePoliciesEnum.CLIPBOARD_READ,
    IframeFeaturePoliciesEnum.CLIPBOARD_WRITE,
  ],
  [ApplicationEntryPointActionFeaturesEnum.DISPLAY_CAPTURE]: [IframeFeaturePoliciesEnum.DISPLAY_CAPTURE],
};

export function buildLegacyAppIframeFeaturePolicies(application: LegacyApplicationModel) {
  return _(application.features)
    .map((f) => legacyApplicationFeaturesToIframeFeaturePoliciesMap[f])
    .compact()
    .flatten()
    .uniq()
    .value();
}

export function buildLegacyApplicationLogger(applicationName: string) {
  return (message: string, data?: unknown) => {
    debug('ui-sdk', `${applicationName}: ${message}`, data);
  };
}

export function findLegacyApplicationAbsoluteUrl(application: LegacyApplicationModel, urlOrPath: string) {
  // If the URL provided is already an absolute URL, return it.
  const absoluteUrl = tryParseUrl(urlOrPath);

  if (absoluteUrl) {
    if (!allowedProtocols.includes(absoluteUrl.protocol)) {
      throw new Error('This protocol is not allowed');
    }
    return absoluteUrl.href;
  }

  if (!application.baseUrl) {
    throw new ApplicationError('This application does not support relative paths.');
  }

  // Remove potential trailing and starting slashes.
  const baseUrl = application.baseUrl.endsWith('/') ? application.baseUrl.slice(0, -1) : application.baseUrl;
  const path = urlOrPath.startsWith('/') ? urlOrPath.substr(1, urlOrPath.length) : urlOrPath;

  // Build it from the application's base URL.
  const computedAbsolutUrl = tryParseUrl(`${baseUrl}/${path}`, globalWindow.location.href);
  if (!computedAbsolutUrl) {
    throw new ApplicationError('Invalid URL.');
  }

  return computedAbsolutUrl.href;
}

const httpVerbToRequestMethod: {[K in HttpVerbsEnum]: RequestMethodsEnum} = {
  [HttpVerbsEnum.GET]: RequestMethodsEnum.GET,
  [HttpVerbsEnum.POST]: RequestMethodsEnum.POST,
  [HttpVerbsEnum.PUT]: RequestMethodsEnum.PUT,
  [HttpVerbsEnum.PATCH]: RequestMethodsEnum.PATCH,
  [HttpVerbsEnum.DELETE]: RequestMethodsEnum.DELETE,
};

export function findHttpRequestMethodFromHttpVerb(httpVerb: HttpVerbsEnum): RequestMethodsEnum {
  return httpVerbToRequestMethod[httpVerb];
}

export function findLegacyTopicTypeForApplication(application: LegacyApplicationModel): string {
  // Custom plugin should register web link
  return isLegacyApplicationCustomPlugin(application) ? 'web' : application.typeId;
}

export function isLegacyApplicationCustomPlugin(application: LegacyApplicationModel) {
  // Custom plugin are serialized as applications using a typeId starting with "custom-".
  const isCustomPlugin = application.typeId.startsWith('custom-');
  // 3rd party integration using the new plugin SDK are serialized as applications with a side panel entry point.
  const isThirdPartyPlugin =
    application.entryPoints.length === 1 &&
    application.entryPoints[0].type === EntryPointTypesEnum.SIDE_PANEL;

  return isCustomPlugin || isThirdPartyPlugin;
}

/*
 * Entry point.
 */

interface LegacyEntryPointBase {
  uid: string;
  applicationId: number;
  applicationTypeId: string;
  entryPointId: string;
  frameType: FrameTypesEnum.ENTRY_POINT;
  frameId: string;
  name: string;
  color: PaletteColorsEnum;
  // TODO: Remove iconUrl in favor of iconBaseUrl and normalized file names.
  iconUrl?: string;
  iconBaseUrl?: string;
}

export interface LegacyConversationLinkDropdownEntryPoint extends LegacyEntryPointBase {
  type: EntryPointTypesEnum.CONVERSATION_LINK_DROPDOWN;
}

export interface LegacyMessageMoreDropdownEntryPoint extends LegacyEntryPointBase {
  type: EntryPointTypesEnum.MESSAGE_MORE_DROPDOWN;
}

export interface LegacySidePanelEntryPoint extends LegacyEntryPointBase {
  type: EntryPointTypesEnum.SIDE_PANEL;
}

export type LegacyEntryPoint =
  | LegacyConversationLinkDropdownEntryPoint
  | LegacyMessageMoreDropdownEntryPoint
  | LegacySidePanelEntryPoint;

/*
 * Entry point instance.
 */

interface LegacyEntryPointInstance<T extends LegacyEntryPoint> {
  id: string;
  entryPoint: T;
}

export type LegacyConversationLinkDropdownEntryPointInstance =
  LegacyEntryPointInstance<LegacyConversationLinkDropdownEntryPoint>;

export type LegacyMessageMoreDropdownEntryPointInstance =
  LegacyEntryPointInstance<LegacyMessageMoreDropdownEntryPoint>;

export function buildLegacyEntryPointInstance<T extends LegacyEntryPoint>(
  entryPoint: T,
): LegacyEntryPointInstance<T> {
  return {
    id: randomString(),
    entryPoint,
  };
}

/*
 * Errors.
 */

export function wrapErrorForLegacyApplication(error: Error): ApplicationError | ApplicationDefaultError {
  if (error instanceof ApplicationError) {
    return error;
  }

  if (error instanceof IOTSError) {
    return new ApplicationError(`Invalid payload: ${error.decodeError}`);
  }

  if (error instanceof CancelError) {
    return new ApplicationError('The process was cancelled.');
  }

  if (error instanceof HttpResponseError) {
    return new ApplicationError(`HTTP request failed (${error.code}).`);
  }

  if (error instanceof HttpError) {
    return new ApplicationError('HTTP request failed.');
  }

  return new ApplicationDefaultError(error);
}
